import React from 'react';
import {Box, Typography} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import {RequestTracker} from '../../../../Component/Model/Dto/Staging/RequestTracker';
import {SalaryBatch} from '../../../../Component/Model/Dto/Staging/SalaryBatch';
import colors from '../../../../assets/theme/base/colors';
import {ImportStep} from './ImportStep';

interface IProps {
    selectedOrganisation: number;
    selectedScorecardIds: string[];
    findScorecardName: (scorecardId: string) => string;
    requestTracker?: RequestTracker.RequestTrackerDetail;
    employeeRecordCount?: number;
    includeSalaries: boolean;
    isImportReady: boolean;
    isImporting: boolean;
    isImportInProgressForOrganisation: boolean;
    disableImport: boolean;
    onBeginImport: () => void;
}

export const ConfirmStep: React.FC<IProps> =
    ({
         selectedOrganisation,
         selectedScorecardIds,
         findScorecardName,
         requestTracker,
         employeeRecordCount,
         includeSalaries,
         isImportReady,
         isImporting,
         isImportInProgressForOrganisation,
         disableImport,
         onBeginImport
     }) => {

        const totalSalaries = requestTracker?.salaryBatch?.totalRecords;
        const scorecardCount = selectedScorecardIds.length;

        const describeSalaryEffect = (coverage: SalaryBatch.ScorecardCoverage): string => {
            if (coverage.recordsInPeriod === 0) {
                return `${coverage.scorecardName} will receive no salary records — all ${totalSalaries} fall outside its reporting period.`;
            }
            return `${coverage.scorecardName} will receive ${coverage.recordsInPeriod} of ${totalSalaries} salary records.`;
        };

        const renderSalaryEffect = () => {
            if (!includeSalaries) {
                return (
                    <Typography variant="body2" color="secondary">
                        Salary records are not part of this import.
                    </Typography>
                );
            }

            if (totalSalaries == null) {
                return (
                    <Typography variant="body2" color="secondary">
                        No salary data was fetched, so no salary records will be imported.
                    </Typography>
                );
            }

            return (
                <>
                    {selectedScorecardIds.map(scorecardId => {
                        const coverage = requestTracker?.salaryCoverage
                            ?.find(item => item.scorecardId === scorecardId);

                        if (!coverage) {
                            return null;
                        }

                        return (
                            <Typography key={scorecardId} variant="body2" color="secondary">
                                {describeSalaryEffect(coverage)}
                            </Typography>
                        );
                    })}
                </>
            );
        };

        const renderEffect = () => {
            if (selectedOrganisation <= 0 || !isImportReady) {
                return (
                    <Typography variant="body2" color="secondary">
                        An import needs a completed fetch. See step 2.
                    </Typography>
                );
            }

            if (scorecardCount === 0) {
                return (
                    <Typography variant="body2" color="secondary">
                        Select at least one scorecard in step 3 to see what this import will do.
                    </Typography>
                );
            }

            return (
                <>
                    <Typography variant="body2" color="secondary">
                        This will import{' '}
                        <strong>
                            {employeeRecordCount != null
                                ? `${employeeRecordCount} employee records`
                                : 'the fetched employee structure'}
                        </strong>
                        {' '}into{' '}
                        <strong>{scorecardCount} scorecard{scorecardCount === 1 ? '' : 's'}</strong>
                        {scorecardCount === 1 ? ` (${findScorecardName(selectedScorecardIds[0])})` : ''}.
                    </Typography>
                    {renderSalaryEffect()}
                </>
            );
        };

        return (
            <ImportStep stepNumber={4} title="Confirm" description="What this import will do.">
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        borderRadius: '5px',
                        backgroundColor: colors.light.main
                    }}>
                    <Box sx={{flex: '1 1 320px'}}>
                        {renderEffect()}
                    </Box>
                    <LoadingButton
                        disabled={disableImport}
                        onClick={onBeginImport}
                        loading={isImporting}
                        color="primary"
                        variant="contained">
                        Begin Import
                    </LoadingButton>
                </Box>

                {isImportInProgressForOrganisation &&
                    <Typography variant="caption" color="secondary">
                        An import is already running for this organisation. You can start another once it finishes.
                    </Typography>
                }
            </ImportStep>
        );
    };
